import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Stack,
  Typography,
  Grid,
  Avatar,
  Chip,
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  Select,
  MenuItem,
  SpeedDialIcon,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Check, FavoriteBorder, Forward10, Hd, MoreHoriz, MoreVert, Pause, PlayArrow, Replay5, Report, ReportGmailerrorred, Shortcut, Speed, ThumbDown, ThumbUp } from "@mui/icons-material";
import { RWebShare } from "react-web-share";
import { isMobile } from "react-device-detect";
import axios from "axios";
import {
  dislikeVideo,
  getFeedbackStatus,
  getVideoDetailsByID,
  getVideoWatchHistory,
  likeVideo,
  reportVideo,
  supportVideo,
} from "../../services/APIServices/videos.api";
import { getChannelByID } from "../../services/APIServices/channel.api";
import { inscribeChannel } from "../../services/APIServices/user.api";
import { useAppSelector } from "../../store/hooks";
import { getUser } from "../../store/reducers/user.reducer";
import { PAGE_ROUTES } from "../../utils/constants";
import { isUserChannel } from "../../utils";
import { VideoResponse } from "../../types/medial";
import { ChannelDetails } from "../../types/channel";
import { useAuth } from "../../context/AuthContext";
import { Slider, IconButton, Box, Menu } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { blockChannel, blockVideo } from "../../services/APIServices/block.api";
import { getVideoURL, getQualityOptions } from "../../utils/videoQuality";

import "../Player/VideoPlayer.css";

// Lazy load ReactPlayer (browser-only)
const ReactPlayer = lazy(() => import("react-player"));

const baseURL = process.env.REACT_APP_SERVER_URL;

function VideoPlayer(props: any) {
  const { videoDetails, setVideoDetails, channel, setChannel } = props;
  let { videoID } = useParams();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const user = useAppSelector((state) => getUser(state));
  const navigate = useNavigate();
  const player = useRef<any>(null);
  // const [quality, setQuality] = useState("360p");
  const [appBarHeight, setAppBarHeight] = useState<any>(10);
  const [playerHeight, setPlayerHeight] = useState<any>(720);
  const [playerWidth, setPlayerWidth] = useState<any>(1280);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [reported, setReported] = useState(false);
  const [supported, setSupported] = useState(false);
  const { verifyAuth, setOnLoginSuccess } = useAuth();
  const [isSpamDialogOpen, setIsSpamDialogOpen] = useState(false);
  const [spamMessage, setSpamMessage] = useState("");
  const [showQualityOptions, setShowQualityOptions] = useState(false);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState("Auto");
  const [selectedSpeed, setSelectedSpeed] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const hideTimeoutRef = useRef<any>(null);
  const [lastWatchedTime, setLastWatchedTime] = useState(0);
  const prevVideoIDRef = useRef<string | null>(null);
  const lastReportedTimeRef = useRef<number>(0);
  const toggleExpand = () => setExpanded(!expanded);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);

  const handleMoreMenuClick = (event: any) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  useEffect(() => {
    fetchVideoDetails();
  }, [videoID]);

  const fetchVideoDetails = async () => {
    if (user?.userID && videoID) {
      try {
        const videoDetails = await getVideoDetailsByID(videoID);
        setVideoDetails(videoDetails);

        getFeedbackStatus(user.userID, videoID).then(
          (feedbackStatus: any) => {
            setLiked(feedbackStatus.like || false);
            setDisliked(feedbackStatus.dislike || false);
            setReported(feedbackStatus.spam || false);
            setSupported(feedbackStatus.support || false);
          }
        );
      } catch (error) {
        console.error("Error fetching video details or feedback status:", error);
      }
    }
  };


  useEffect(() => {
    setAppBarHeight(document.getElementById("App-Bar")?.offsetTop);
  }, []);

  useEffect(() => {
    setLoading(true);
    if (videoDetails !== null && channel !== null) {
      setLoading(false);
    }
  }, [videoDetails]);

  useEffect(() => {
    // Always set to Auto as default for all videos
    setSelectedQuality("Auto");
  }, [videoDetails?.videoID]);

  const handleInscribe = () => {
    inscribeChannel(channel?.channelID as string, user?.userID).then(
      (res: any) => {
        let status = res?.message?.split(" ")?.[0];
        if (status && channel) {
          getChannelByID(videoDetails?.channelID as string, user?.userID).then(
            (res: any) => {
              setChannel(res);
              setLoading(false);
            }
          );
        }
      }
    );
  };

  const handleSubscribeClick = () => {
    if (verifyAuth()) {
      handleInscribe();
    } else {
      setOnLoginSuccess(handleInscribe);
    }
  };

  const handleDislike = () => {
    dislikeVideo(user?.userID as string, videoDetails?.videoID as string).then(
      (res: any) => {
        fetchVideoDetails();
        getVideoDetailsByID(videoDetails?.videoID as string).then(
          (res: any) => {
            setVideoDetails(res);
          }
        );
      }
    );
  };
  const handleDislikeClick = () => {
    if (verifyAuth()) {
      handleDislike();
    } else {
      setOnLoginSuccess(handleDislike);
    }
  };

  const handleSupport = () => {
    const watchTimeSeconds = Math.floor(currentTime);
    supportVideo(user?.userID as string, videoDetails?.videoID as string, watchTimeSeconds).then(
      (res: any) => {
        fetchVideoDetails();
        getVideoDetailsByID(videoDetails?.videoID as string).then(
          (res: any) => {
            setVideoDetails(res);
          }
        );
      }
    );
  };

  const handleSupportClick = () => {
    if (verifyAuth()) {
      handleSupport();
    } else {
      setOnLoginSuccess(handleSupport);
    }
  };

  const handleReport = (video: any) => {
    if (verifyAuth()) {
      setSelectedReason("");
      setCustomReason("");
      setIsReportDialogOpen(true);
    } else {
      setOnLoginSuccess(() => handleReport(video));
    }
  };

  const handleReportSubmit = (video: any) => {
    try {
      if (!selectedReason) {
        toast.warning("Please select a reason before submitting.");
        return;
      }

      const reason =
        selectedReason === "Other" && customReason.trim()
          ? `Other: ${customReason.trim()}`
          : selectedReason;

      const supportEmail = "contact@indeeclips.com";
      const subject = "Video Report";
      const body = [
        "Hello IndeeClips Team,",
        "",
        "I found an issue with the following video on the platform:",
        "",
        `Video Title: ${video?.title || "N/A"}`,
        `Channel Name: ${video?.channelName || "N/A"}`,
        "",
        `Reported Reason: ${reason}`,
        "",
        "Kindly look into this matter at the earliest and take the necessary action.",
        "",
        `Thanks & Regards,`,
        `${user?.email || "IndeeClips User"}`
      ].join("\n");

      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(body);

      const isMobile = /Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent || ""
      );

      if (isMobile) {
        window.location.href = `mailto:${supportEmail}?subject=${encodedSubject}&body=${encodedBody}`;
      } else {
        const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${supportEmail}&su=${encodedSubject}&body=${encodedBody}`;
        window.open(gmailUrl, "_blank", "noopener,noreferrer");
      }

      setIsReportDialogOpen(false);
      toast.success("Redirecting to email...");
    } catch (error) {
      console.error("Error while reporting video:", error);
      toast.error("Failed to open email. Please try again.");
    }
  };

  const handleSpamClick = () => {
    if (verifyAuth()) {
      setIsSpamDialogOpen(true);
    } else {
      setOnLoginSuccess(() => setIsSpamDialogOpen(true));
    }
  };

  const handleLike = () => {
    likeVideo(user?.userID as string, videoDetails?.videoID as string).then(
      (res: any) => {
        fetchVideoDetails();
        getVideoDetailsByID(videoDetails?.videoID as string).then(
          (res: any) => {
            setVideoDetails(res);
          }
        );
      }
    );
  };
  const handleLikeClick = () => {
    if (verifyAuth()) {
      handleLike();
    } else {
      setOnLoginSuccess(handleLike);
    }
  };
  const handlePlayerReady = () => {
    setPlayerHeight(document.getElementById("player-wrapper")?.offsetHeight);
    setPlayerWidth(document.getElementById("player-wrapper")?.offsetWidth);
    setLoading(false);
    setPlaying(!((videoDetails?.age >= 18 && user?.age < 18) || (videoDetails?.deleted)));
    setShowQualityOptions(false);
    setShowSpeedOptions(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChannelClick = () => {
    if (channel?.channelID) {
      navigate(`${PAGE_ROUTES.CHANNEL_PAGE}/profile/${channel?.channelID}`);
    }
  };

  const formatNumber = (value: number | undefined, suffix: string): string => {
    if (value === undefined) return `0 ${suffix}`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)}B ${suffix}`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M ${suffix}`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(1)}K ${suffix}`;
    return `${value} ${suffix}`;
  };

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectOption, setSelectOption] = useState<String | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [subAnchorEl, setSubAnchorEl] = useState<HTMLElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const [isBuffering, setIsBuffering] = useState(false);

  const [playbackRate, setPlaybackRate] = useState(1);
  const [quality, setQuality] = useState("medium");
  const [showControls, setShowControls] = useState(true);

  const optionsOpen = Boolean(anchorEl);
  const subOptionsOpen = Boolean(subAnchorEl);

  const togglePlayPause = () => setPlaying(!playing);
  const toggleMute = () => setMuted(!muted);

  const handleFullscreenToggle = () => {
    const videoContainer = document.getElementById("video-container");
    if (!videoContainer) return;

    if (!document.fullscreenElement) {
      videoContainer.requestFullscreen().then(() => {
        const screenOrientation = window.screen.orientation as any;
        if (screenOrientation.lock) {
          screenOrientation.lock("landscape").catch((err: any) => console.log("Orientation error:", err));
        }
      });
    } else {
      document.exitFullscreen();
      const screenOrientation = window.screen.orientation as any;
      if (screenOrientation.unlock) {
        screenOrientation.unlock();
      }
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time) || time < 0) {
      return "00:00";
    }

    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };


  useEffect(() => {
    const interval = setInterval(() => {
      if (player.current) {
        const current = player.current.getCurrentTime();
        const total = player.current.getDuration();
        setCurrentTime(current);
        setDuration(total);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const handleQualityChange = (q: string) => {
    setLoading(true);
    if (player.current) {
      const current = player.current.getCurrentTime();
      setCurrentTime(current);
      setSelectedQuality(q);

      setTimeout(() => {
        if (player.current) {
          player.current.seekTo(current);
          setLoading(false);
        }
      }, 10);
    }
    setShowQualityOptions(false);
  };

  const handleSpeedChange = (speed: any) => {
    setLoading(true);
    setSelectedSpeed(speed);
    setShowSpeedOptions(false);
    setPlaybackRate(speed);
    setLoading(false);
  };

  const seek = (seconds: number) => {
    if (player.current) {
      const newTime = player.current.getCurrentTime() + seconds;
      player.current.seekTo(newTime, "seconds");
    }
  };

  // 🧠 Debounced save function — run only once when needed
  const saveWatchProgress = async (id: string, time: number, dur: number) => {
    if (!id || time <= 0 || dur <= 0) return;
    try {
      await getVideoWatchHistory(id, Math.floor(time), Math.floor(dur));
      console.log("✅ Watch progress updated for:", id, time, "/", dur);
    } catch (err) {
      console.error("❌ Error updating watch progress", err);
    }
  };

  // ✅ 1. When video changes (old → new)
  useEffect(() => {
    if (
      prevVideoIDRef.current &&
      prevVideoIDRef.current !== videoID &&
      lastReportedTimeRef.current > 0 &&
      duration > 0
    ) {
      console.log("🎬 When video changes");
      saveWatchProgress(
        prevVideoIDRef.current,
        lastReportedTimeRef.current,
        duration
      );
    }

    prevVideoIDRef.current = videoID ?? null;
  }, [videoID]);

  // ✅ 2. On component unmount (page change or route leave)
  useEffect(() => {
    return () => {
      // Prevent double call — only run if video is same as previous
      if (
        prevVideoIDRef.current === videoID &&
        videoID &&
        lastReportedTimeRef.current > 0 &&
        duration > 0
      ) {
        console.log("🧹 On component unmount (page change)");
        saveWatchProgress(videoID, lastReportedTimeRef.current, duration);
      }
    };
    // ✅ Empty deps → only runs once per component lifecycle
  }, []);

  // // ✅ 3. Before unload (tab close / refresh)
  // useEffect(() => {
  //   const handleBeforeUnload = () => {
  //     if (videoID && lastReportedTimeRef.current > 0 && duration > 0) {
  //       console.log("🚪 Before unload (tab close / refresh)");
  //       navigator.sendBeacon(
  //         "https://indeeclips.com/api/rest/watch-history/update-progress",
  //         JSON.stringify({
  //           videoID,
  //           currentTimeSeconds: Math.floor(lastReportedTimeRef.current),
  //           videoDurationSeconds: Math.floor(duration),
  //         })
  //       );
  //     }
  //   };

  //   window.addEventListener("beforeunload", handleBeforeUnload);
  //   return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  // }, [videoID, duration]);

  // ✅ 4. Track progress but store only every few seconds
  const handleProgress = ({ playedSeconds }: { playedSeconds: number }) => {
    setLastWatchedTime(playedSeconds);

    if (playedSeconds - lastReportedTimeRef.current >= 5) {
      lastReportedTimeRef.current = playedSeconds;
    }
  };

  const handleBlockVideo = async (videoID: string) => {
    if (!verifyAuth()) {
      setOnLoginSuccess(() => handleBlockVideo(videoID));
      return;
    }

    try {
      await blockVideo(videoID);

      toast.success("Video blocked successfully");
    } catch (err) {
      console.error(err);
      toast.error("Failed to block video");
    }
  };

  const handleBlockChannel = async (channelID: string) => {
      if (!verifyAuth()) {
        setOnLoginSuccess(() => handleBlockChannel(channelID));
        return;
      }
      try {
        await blockChannel(channelID);
        toast.success("Channel blocked successfully");
      } catch (err) {
        console.error(err);
        toast.error("Failed to block channel");
      }
    };

  // Don't render player on server
  if (typeof window === 'undefined') {
    return (
      <Box sx={{ aspectRatio: '16/9', bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: "blue" }} />
      </Box>
    );
  }

  return (
    <>
      <div onContextMenu={(e) => e.preventDefault()}>
        <div
          id={"player-wrapper"}
          className={`player-wrapper ${isMobile ? "fixed-top" : ""}`}
          style={{
            top: (appBarHeight - 25) as any,
            backgroundColor: "black",
            zIndex: 999,
          }}
        >
          <Box
            id="video-container"
            position="relative"
            width="100%"
            height="100%"
            bgcolor="black"
            onMouseEnter={() => !isMobile && setShowControls(true)}
            onMouseLeave={() => !isMobile && setShowControls(false)}
            onClick={() => {
              if (isMobile) {
                setShowControls(true);
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = setTimeout(() => {
                  setShowControls(false);
                }, 3000); // hide after 3 sec
              }
            }}
          >
            {/* Blue Circle Loader */}
            {loading && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                sx={{ transform: "translate(-50%, -50%)" }}
              >
                <CircularProgress sx={{ color: "blue" }} />
              </Box>
            )}

            {/* Video Player - Lazy loaded */}
            <Suspense fallback={<CircularProgress sx={{ color: "blue" }} />}>
              <ReactPlayer
                ref={player}
                url={getVideoURL(baseURL!, videoID!, videoDetails?.hlsProcessed || false, selectedQuality)}
                className={`react-player ${isMobile ? "absolute" : ""}`}
                onReady={() => handlePlayerReady()}
                onProgress={handleProgress}
                onDuration={(d: number) => setDuration(d)}
                width="100%"
                height="100%"
                playing={playing}
                muted={muted}
                controls={false}
                playbackRate={playbackRate}
                config={{ file: { attributes: { controlsList: "nodownload" } } }}
              />
            </Suspense>

            {videoDetails?.age >= 18 && user?.age < 18 && (
              <Box
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                bgcolor="rgb(0, 0, 0)"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                color="white"
                textAlign="center"
                zIndex={10}
              >
                <Typography variant="body2">
                  {/* This video content is for age 18+. You are not allowed to watch this. */}
                  {t("ageRestricted")}
                </Typography>
              </Box>
            )}

            {videoDetails && videoDetails?.deleted && (
              <Box
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                bgcolor="rgb(0, 0, 0)"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                color="white"
                textAlign="center"
                zIndex={10}
              >
                <Typography variant="body2">
                  {t("videoDeletedMessage")}
                </Typography>
              </Box>
            )}
            {/* Controls */}
            {showControls && (

              <>
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  sx={{
                    transform: "translate(-50%, -50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? "20px" : "50px",
                  }}
                >
                  <IconButton onClick={() => seek(-5)} sx={{ color: "white", fontSize: isMobile ? 20 : 40 }}>
                    <Replay5 fontSize={isMobile ? "small" : "large"} />
                  </IconButton>

                  <IconButton onClick={togglePlayPause} sx={{ color: "white", fontSize: isMobile ? 30 : 50 }}>
                    {playing ? <Pause fontSize={isMobile ? "small" : "large"} /> : <PlayArrow fontSize={isMobile ? "small" : "large"} />}
                  </IconButton>

                  <IconButton onClick={() => seek(10)} sx={{ color: "white", fontSize: isMobile ? 20 : 40 }}>
                    <Forward10 fontSize={isMobile ? "small" : "large"} />
                  </IconButton>
                </Box>
                <Box
                  position="absolute"
                  bottom={0}
                  left={0}
                  width="100%"
                  color="white"
                  // px={2}
                  // pt={1}
                  // pb={isMobile ? 0.5 : 1}
                  paddingLeft={"6px"}
                  paddingRight={"14px"}
                  paddingTop={"8px"}
                  paddingBottom={"14px"}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={0}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      {/* Left Controls */}
                      <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
                        <IconButton
                          onClick={togglePlayPause}
                          color="inherit"
                          sx={{ fontSize: isMobile ? 20 : 28 }}
                        >
                          {playing ? (
                            <PauseIcon fontSize={isMobile ? "small" : "medium"} />
                          ) : (
                            <PlayArrowIcon fontSize={isMobile ? "small" : "medium"} />
                          )}
                        </IconButton>

                        <Typography
                          fontSize={isMobile ? "0.75rem" : "1rem"}
                          sx={{ minWidth: 70, textAlign: "center" }}
                        >
                          {formatTime(currentTime)} / {formatTime(duration)}
                        </Typography>
                      </Box>

                      {/* Right Controls */}
                      <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2}>
                        <Tooltip title={muted ? "Unmute" : "Mute"}>
                          <IconButton color="inherit" sx={{ fontSize: isMobile ? 20 : 28 }} onClick={toggleMute}>
                            {muted ? (
                              <VolumeOffIcon fontSize={isMobile ? "small" : "medium"} />
                            ) : (
                              <VolumeUpIcon fontSize={isMobile ? "small" : "medium"} />
                            )}
                          </IconButton>
                        </Tooltip>

                        {/* Speed Icon */}
                        <Tooltip title="Playback Speed">
                          <IconButton onClick={() => { setShowSpeedOptions(!showSpeedOptions); setShowQualityOptions(false); }} color="inherit" sx={{ fontSize: isMobile ? 20 : 28 }}>
                            <Speed fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Tooltip>
                        {showSpeedOptions && (
                          <Box className="popup" position="absolute" bottom={isMobile ? 70 : 80} right={isMobile ? 60 : 90} bgcolor="black" color="white" p={1} borderRadius={2} width={105} maxHeight={150} overflow="auto">
                            {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((speed) => (
                              <Box key={speed} p={1} display="flex" justifyContent="space-between" alignItems="center" onClick={() => handleSpeedChange(speed)}>
                                {selectedSpeed === speed && <Check fontSize="small" />}
                                {speed}x
                              </Box>
                            ))}
                          </Box>
                        )}

                        <Tooltip title="Video Quality">
                          <IconButton onClick={() => { setShowQualityOptions(!showQualityOptions); setShowSpeedOptions(false); }} color="inherit" sx={{ fontSize: isMobile ? 20 : 28 }}>
                            <Hd fontSize={isMobile ? "small" : "medium"} />
                          </IconButton>
                        </Tooltip>
                        {showQualityOptions && (
                          <Box className="popup" position="absolute" bottom={isMobile ? 70 : 80} right={isMobile ? 30 : 50} bgcolor="black" color="white" p={1} borderRadius={2}>
                            {getQualityOptions().map((qualityOption) => (
                              <Box
                                key={qualityOption}
                                p={1}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                onClick={() => handleQualityChange(qualityOption)}
                                sx={{ cursor: "pointer" }}
                              >
                                {selectedQuality === qualityOption && (
                                  <Check fontSize="small" />
                                )}
                                {qualityOption}
                              </Box>
                            ))}
                          </Box>
                        )}

                        <Tooltip title={fullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                          <IconButton onClick={handleFullscreenToggle} color="inherit" sx={{ fontSize: isMobile ? 20 : 28 }}>
                            {fullscreen ? (
                              <FullscreenExitIcon fontSize={isMobile ? "small" : "medium"} />
                            ) : (
                              <FullscreenIcon fontSize={isMobile ? "small" : "medium"} />
                            )}
                          </IconButton>
                        </Tooltip>
                        {/* Quality and Playback Speed Icons */}
                      </Box>
                    </Box>

                    <Slider
                      value={(currentTime / duration) * 100 || 0}
                      onChange={(_, value: any) => {
                        if (player.current) {
                          player.current.seekTo((value / 100) * duration);
                        }
                      }}
                      sx={{
                        color: "blue",
                        height: 4,
                        padding: isMobile ? "2px !important" : "0px",
                        mt: 0.5,
                        mb: 0,
                        "& .MuiSlider-track": {
                          backgroundColor: "blue",
                        },
                        "& .MuiSlider-rail": {
                          backgroundColor: "white",
                          opacity: 1,
                        },
                        "& .MuiSlider-thumb": {
                          display: "none",
                        },
                      }} />
                  </Box>
                </Box>
              </>
            )}
          </Box>

        </div>
        {isMobile ? (
          <div
            className={"player-placeholder"}
            style={{
              paddingBottom: `${100 / (playerWidth / playerHeight)}%`,
            }}
          />
        ) : null}
        {!loading ? (
          <div>
            <Stack>
              <Typography gutterBottom variant="h6" sx={{ fontWeight: "bold" }}>
                {videoDetails?.title}
              </Typography>
              <Typography
                gutterBottom
                variant="caption"
                sx={{ color: "GrayText" }}
              >
                {formatNumber(videoDetails?.views, t("videoCard.views"))}
              </Typography>
            </Stack>
            <Stack
              direction={"row"}
              sx={{ alignItems: "center", marginTop: 2 }}
            >
              <Grid container rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack direction={"row"} sx={{ alignItems: "center" }}>
                    <Avatar
                      alt={""}
                      src={channel?.profilePic as string}
                      sx={{
                        width: 56,
                        height: 56,
                        marginRight: 2,
                        cursor: "pointer",
                      }}
                      onClick={handleChannelClick}
                    />
                    <Stack direction={"column"}>
                      <Typography
                        gutterBottom
                        variant="body1"
                        onClick={handleChannelClick}
                        sx={{ cursor: "pointer" }}
                      >
                        {channel?.name}
                      </Typography>
                      <Typography
                        gutterBottom
                        variant="caption"
                        sx={{ color: "GrayText" }}
                      >
                        {t("subscribers.subscriberCountLabel", {
                          count: channel?.subscriberCount ?? 0
                        })}
                      </Typography>
                    </Stack>
                    <Chip
                      size="medium"
                      color="secondary"
                      label={(typeof channel?.isSubscribed === "boolean" &&
                        typeof channel?.isSubscribed) ||
                        channel?.isSubscribed === "SUBSCRIBED"
                        ? t("subscribers.unsubscribe")
                        : t("subscribers.subscribe")
                      }
                      sx={{
                        cursor: "pointer",
                        marginInline: 2,
                        marginLeft: "auto",
                        right: 0,
                      }}
                      onClick={() => handleSubscribeClick()}
                      disabled={isUserChannel(
                        user?.channelList,
                        channel?.channelID
                      )}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={5} sx={{ alignItems: "center" }}>
                  <Stack
                    direction={"row"}
                    sx={{
                      height: "100%",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "space-evenly",
                    }}
                    gap={"4px"}
                  >
                    {/* Support Button */}
                    <Chip
                      icon={<FavoriteBorder />}
                      label={t("support")}
                      sx={{
                        marginRight: 1,
                        backgroundColor: supported ? "#027fff" : "",
                        color: supported ? "white" : "",
                        cursor: currentTime < 60 ? "not-allowed" : "pointer",
                        opacity: currentTime < 60 ? 0.5 : 1,
                      }}
                      onClick={() => {
                        if (currentTime >= 60) {
                          handleSupportClick();
                        }
                      }}
                      disabled={
                        currentTime < 60 ||
                        isUserChannel(user?.channelList, channel?.channelID)
                      }
                    />

                    <Chip
                      icon={<ThumbUp />}
                      label={formatNumber(videoDetails?.likes, "")}
                      sx={{
                        marginRight: 1,
                        backgroundColor: liked ? "#027fff" : "",
                        color: liked ? "white" : "",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        handleLikeClick();
                      }}
                      disabled={isUserChannel(
                        user?.channelList,
                        channel?.channelID
                      )}
                    />
                    <Chip
                      icon={<ThumbDown />}
                      label={formatNumber(videoDetails?.disLikes, "")}
                      sx={{
                        marginRight: 1,
                        backgroundColor: disliked ? "#027fff" : "",
                        color: disliked ? "white" : "",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        handleDislikeClick();
                      }}
                      disabled={isUserChannel(
                        user?.channelList,
                        channel?.channelID
                      )}
                    />
                    <Chip
                      icon={<Report />}
                      label={formatNumber(videoDetails?.spam || 0, "")}
                      onClick={() => {
                        handleSpamClick();
                      }}
                      disabled={isUserChannel(
                        user?.channelList,
                        channel?.channelID
                      )}
                      sx={{
                        backgroundColor: (() => {
                          const spamCount = videoDetails?.spam || 0;
                          if (spamCount < 50) return "green";
                          if (spamCount >= 50 && spamCount < 100) return "yellow";
                          if (spamCount >= 100 && spamCount < 200) return "red";
                          return "darkred";
                        })(),
                        color: "white",
                        cursor: "pointer",
                      }}
                    />

                    <RWebShare
                      data={{
                        text: videoDetails?.description,
                        url: `${typeof window !== 'undefined' ? window.location.origin : 'https://indeeclips.com'}/watch/${videoDetails?.videoID}?type=video`,
                        title: videoDetails?.title,
                      }}
                    >
                      <Chip
                        icon={<Shortcut />}
                        label={t("videoCard.share")}
                        onClick={() => { }}
                      />
                    </RWebShare>

                    {/* ⋯ More Options */}
                    <Chip
                      icon={<MoreHoriz />}
                      label={t("nav.More")}
                      onClick={handleMoreMenuClick}
                      sx={{
                        backgroundColor: "#f5f5f5",
                        color: "#333",
                        fontWeight: 500,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#e0e0e0" },
                      }}
                    />

                    {/* Dropdown for Report / Block */}
                    <Menu
                      anchorEl={moreMenuAnchor}
                      open={Boolean(moreMenuAnchor)}
                      onClose={handleMoreMenuClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleReport(videoDetails);
                          handleMoreMenuClose();
                        }}
                        disabled={isUserChannel(user?.channelList, channel?.channelID)}
                      >
                        <ReportGmailerrorred
                          sx={{ color: "#e53935", fontSize: 20, mr: 1 }}
                        />
                        {t("videoCard.report")}
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleBlockVideo(videoDetails?.videoID);
                          handleMoreMenuClose();
                        }}
                        disabled={isUserChannel(user?.channelList, channel?.channelID)}
                      >
                        <Report sx={{ color: "#757575", fontSize: 20, mr: 1 }} />
                        {t("videoCard.blockVideo")}
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleBlockChannel(channel?.channelID || "");
                          handleMoreMenuClose();
                        }}
                        disabled={isUserChannel(user?.channelList, channel?.channelID)}
                      >
                        <Report sx={{ color: "#757575", fontSize: 20, mr: 1 }} />
                        {t("videoCard.blockChannel")}
                      </MenuItem>
                    </Menu>
                  </Stack>
                </Grid>
                <Card
                  sx={{
                    mt: 2,
                    p: 2,
                    width: "100%",
                    border: "1px solid #ddd",
                    backgroundColor: "#f9f9f9",
                    borderRadius: 2,
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "left",
                      display: "-webkit-box",
                      WebkitLineClamp: expanded ? "unset" : 3, // Show only 3 lines initially
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      whiteSpace: expanded ? "normal" : "nowrap",
                    }}
                  >
                    {videoDetails?.description?.trim() || t("noDescription")}
                  </Typography>

                  {videoDetails?.description && videoDetails?.description.length > 100 && ( // Only show button if description is long
                    <Button
                      size="small"
                      sx={{ mt: 1, textTransform: "none", color: "#1976d2", fontWeight: 500 }}
                      onClick={toggleExpand}
                    >
                      {t(expanded ? "showLess" : "showMore")}
                    </Button>
                  )}
                </Card>
              </Grid>
            </Stack>
          </div>
        ) : (
          <>
            <Stack>
              <Skeleton
                variant="text"
                width={"100%"}
                sx={{ fontSize: "1.5rem" }}
              />
            </Stack>
            <Stack
              direction={"row"}
              sx={{ alignItems: "center", marginTop: 2 }}
            >
              <Grid container>
                <Grid item>
                  <Stack direction={"row"}>
                    <Skeleton
                      variant="circular"
                      width={56}
                      height={56}
                      sx={{ marginRight: 2 }}
                    />
                    <Stack direction={"column"}>
                      <Skeleton variant="text" width={150} />
                      <Skeleton variant="text" width={100} />
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </>
        )}

        <Dialog open={isReportDialogOpen} onClose={() => setIsReportDialogOpen(false)}>
          <DialogTitle>{t("reportDialog.title")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t("reportDialog.description")}
            </Typography>

            <TextField
              select
              fullWidth
              label={t("reportDialog.selectReasonLabel")}
              value={selectedReason}
              onChange={(e) => setSelectedReason(e.target.value)}
              sx={{ mb: selectedReason === "Other" ? 2 : 0 }}
            >
              {(t("reportDialog.reasons", { returnObjects: true }) as any).map((reason: string, idx: number) => (
                <MenuItem key={idx} value={reason}>
                  {reason}
                </MenuItem>
              ))}
            </TextField>

            {selectedReason === "Other" && (
              <TextField
                fullWidth
                label={t("reportDialog.enterReasonLabel")}
                multiline
                rows={3}
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
              />
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setIsReportDialogOpen(false)}>
              {t("reportDialog.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={() => handleReportSubmit(videoDetails)}
              disabled={
                !selectedReason ||
                (selectedReason === "Other" && !customReason.trim())
              }
            >
              {t("reportDialog.submit")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isSpamDialogOpen} onClose={() => setIsSpamDialogOpen(false)}>
          <DialogTitle>{t(reported ? "removeSpam" : "reportSpam")}</DialogTitle>
          <DialogContent>
            {reported ? (
              <Typography>{t("removeReportConfirmation")}</Typography>
            ) : (
              <TextField
                label={t("enterReason")}
                fullWidth
                multiline
                rows={4}
                value={spamMessage}
                onChange={(e) => setSpamMessage(e.target.value)}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsSpamDialogOpen(false)}>{t("cancel")}</Button>
            <Button
              onClick={handleReport}
              color="primary"
              disabled={!reported && !spamMessage.trim()}
            >
              {t(reported ? "remove" : "submit")}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
}

export default VideoPlayer;
